import React, { FC, useEffect, useRef, useState } from "react";

import { Box } from "@UI/Components/Box/Box";
import { Button } from "@UI/Components/Button/Button";
import { Icon } from "@UI/Components/Icon/Icon";
import { Plus } from "@UI/Components/Icons/Plus";
import { SimpleGrid } from "@UI/Components/SimpleGrid/SimpleGrid";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import useI18n from "@I18n/hooks/useI18n";

import { usePhotoAttachments } from "@Shared/hooks/usePhotoAttachments";
import { PhotoAttachmentId } from "@Shared/services/photoAttachments";

import { IPhotoAttachment } from "../../../types";
import { usePhotoSelection } from "../../../hooks/usePhotoSelection";
import { usePhotoToast } from "../../../hooks/usePhotoToast";
import messages from "../../../messages";
import { PhotoCard } from "./PhotoCard";
import { PhotoCardSkeleton } from "./PhotoCardSkeleton";
import { PhotoLightbox } from "./PhotoLightbox";

export interface PhotoUploaderProps {
  /** The attachment ids already on the draft — the form value, and all of it:
   * a caption belongs to the attachment, never to the answer. */
  value: number[];
  /** Called with the new id list once an upload or a removal has been accepted
   * by the server — never with anything still in flight. */
  onChange: (value: number[]) => void;
  /** Called once a caption has been written to its attachment, so the caller
   * can put it in the draft it holds — the screen re-seeds from there when the
   * user leaves and comes back before the draft is fetched again. */
  onDescriptionSaved?: (id: number, description: string) => void;
  /** `draft.photo_attachments` — where a photo's thumbnail *and* its caption
   * come from, since the form value is ids alone (see `types.ts`). */
  existingPhotos?: IPhotoAttachment[];
  /** Merges a freshly uploaded/removed photo into the cached draft, so its
   * thumbnail survives this component unmounting (Continue navigates away,
   * Back remounts it) — see `useRecordUploadedPhoto`. */
  onPhotoUploaded?: (photo: IPhotoAttachment) => void;
  onPhotoRemoved?: (id: number) => void;
  /** The draft's own id, threaded through for whenever these photos get
   * linked to it — not needed to upload, since `task_photos` attachments
   * start unattached (see `usePhotoAttachments`). */
  photoAttachableId?: PhotoAttachmentId;
  category: string;
  /** `constraints.max_files` — the upload affordance stops at this count. */
  maxFiles: number;
  /** `constraints.allowed_extensions`, lowercase and without the dot. */
  allowedExtensions: string[];
  /** `constraints.caption_max_length` — the only photo rule in the schema. */
  captionMaxLength: number;
  /** Reported while an upload or a removal is in flight, so the screen can
   * hold Continue until it's done. */
  onBusyChange?: (isBusy: boolean) => void;
  isDisabled?: boolean;
  /** Defaults to the wizard's own two-up layout on larger screens. A caller
   * showing this in a narrower context (e.g. a modal) can pin it to one
   * column at every breakpoint instead. */
  columns?: React.ComponentProps<typeof SimpleGrid>["columns"];
  /** Leaves removals and captions to the caller instead of writing them to
   * the server: a removal only drops the card (`onPhotoRemoved`), a caption
   * is only handed over on blur (`onDescriptionSaved`). For a caller that
   * applies every change at once on its own confirm (e.g. the task show's
   * "Add photos" modal). Uploads still run immediately, for the preview. */
  defersServerWrites?: boolean;
  /** Lifts each photo card off its background — see `PhotoCard`. Needed by a
   * caller whose own background is white (e.g. a modal), not by the wizard. */
  isElevated?: boolean;
}

/**
 * The photos screen's uploader: an "Add photos" picker, a card per attached
 * photo (thumbnail, caption, remove), and a skeleton card per file still
 * uploading.
 *
 * A caption is stored on the attachment itself (`metadata.description`, see
 * `usePhotoAttachments`), written as soon as its field is left rather than
 * held until Continue — the photo it belongs to has been uploaded since the
 * moment it was picked, so a caption left unsaved was the one thing a visitor
 * could lose by going back. The answer carries ids and nothing else, which is
 * also all the backend takes (`validate_picture_upload`).
 *
 * So `value` is what gets saved, while everything a photo *looks* like —
 * thumbnail and caption — comes from `existingPhotos` (the draft's own
 * photos), or, for one uploaded or captioned during this mount, from
 * `sessionThumbUrls` / `sessionCaptions` until the draft carries it.
 *
 * A freshly uploaded photo's thumbnail is the browser's own local preview of
 * the file (a `blob:` URL), not the server's `thumbnailUrl` — that one points
 * at a processed image variant a background job hasn't generated yet, so it
 * 404s until well after this component would have shown it. The portfolio
 * photo book (`/listworkers/profil/portfolio`) uses the same trick for the
 * same reason.
 *
 * Removal is immediate: the card disappears once the DELETE it fired
 * actually succeeds, with no confirmation step in between — unless
 * `defersServerWrites` hands both removals and captions to the caller.
 */
export const PhotoUploader: FC<PhotoUploaderProps> = ({
  value,
  onChange,
  onDescriptionSaved,
  existingPhotos,
  onPhotoUploaded,
  onPhotoRemoved,
  photoAttachableId,
  category,
  maxFiles,
  allowedExtensions,
  captionMaxLength,
  onBusyChange,
  isDisabled = false,
  columns = { base: 1, lg: 2 },
  defersServerWrites = false,
  isElevated = false,
}) => {
  const { formatMessage } = useI18n();
  const showPhotoToast = usePhotoToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Latest `value`/`onChange` for the upload/remove callbacks below, which run
  // to completion on their own schedule (a presign/S3/attach or DELETE round
  // trip) and must never act on a `value` that was current only when the user
  // clicked — a second photo added meanwhile would otherwise be dropped.
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const [sessionThumbUrls, setSessionThumbUrls] = useState<
    Record<number, string>
  >({});
  // Captions typed during this mount. The attachment is where they are saved,
  // but the field has to render every keystroke, and the draft only learns of
  // one once its blur has been accepted.
  const [sessionCaptions, setSessionCaptions] = useState<
    Record<number, string>
  >({});
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);

  // What a removal leaves behind, whether the server's DELETE just succeeded
  // or `defersServerWrites` leaves that DELETE to the caller.
  const dropPhoto = (id: number) => {
    onChangeRef.current(valueRef.current.filter((photoId) => photoId !== id));
    setSessionThumbUrls((current) => {
      const { [id]: removedUrl, ...rest } = current;
      if (removedUrl) URL.revokeObjectURL(removedUrl);
      return rest;
    });
    setSessionCaptions((current) => {
      const rest = { ...current };
      delete rest[id];

      return rest;
    });
    onPhotoRemoved?.(id);
  };

  // Passed to the hook as its own truncation floor; `handleFilesSelected`
  // below additionally accounts for `pendingUploads` (uploads this same hook
  // already has in flight), which the hook has no way to fold into this
  // number itself — it is this render's output, not an input to this call.
  const remainingSlots = maxFiles - value.length;

  const { upload, remove, saveDescription, removingId, pendingUploads } = usePhotoAttachments({
    category,
    photoAttachableId,
    remainingSlots,
    onUploadSuccess: (photo, previewUrl) => {
      const id = Number(photo.id);

      setSessionThumbUrls((current) => ({ ...current, [id]: previewUrl }));
      onChangeRef.current([...valueRef.current, id]);
      // The preview, not the server's own (not-yet-processed) URL — see the
      // component doc above — persisted past this mount via the draft cache.
      onPhotoUploaded?.({
        id,
        thumb_url: previewUrl,
        normal_url: previewUrl,
        description: "",
      });
    },
    onUploadError: () =>
      showPhotoToast(
        formatMessage(messages["taskCreation.photos.uploadError"])
      ),
    onRemoveSuccess: (id) => dropPhoto(Number(id)),
    onRemoveError: () =>
      showPhotoToast(
        formatMessage(messages["taskCreation.photos.removeError"])
      ),
  });

  // A submit mid-upload would save a `value` missing the photo still in
  // flight; mid-removal, one the server is about to delete. Either race is
  // what `onBusyChange` exists to prevent — see `IQuestionAdapterProps`.
  const isBusy = pendingUploads.length > 0 || removingId != null;
  useEffect(() => {
    onBusyChange?.(isBusy);
  }, [isBusy]);

  const photoUrlFor = (id: number): string =>
    sessionThumbUrls[id] ??
    existingPhotos?.find((photo) => photo.id === id)?.thumb_url ??
    existingPhotos?.find((photo) => photo.id === id)?.normal_url ??
    "";

  // What this mount typed wins over what the draft was fetched with: the
  // attachment already has it, the draft's copy is simply older.
  const captionFor = (id: number): string =>
    sessionCaptions[id] ??
    existingPhotos?.find((photo) => photo.id === id)?.description ??
    "";

  // `photoAttachableId` isn't needed to upload — `task_photos` starts
  // unattached and is linked to the task later, at submit time.
  const availableNow = remainingSlots - pendingUploads.length;
  const canAddMore = availableNow > 0;

  const { selectFiles, notifyLimitReached } = usePhotoSelection({
    allowedExtensions,
    maxFiles,
    availableSlots: availableNow,
  });

  const handleFilesSelected = (files: FileList | null) => {
    // Whatever `selectFiles` drops, it has already explained — an empty result
    // means the user has been told why nothing is happening.
    const accepted = selectFiles(files);

    if (accepted.length > 0) upload(accepted);
  };

  const viewerSlides = value.map((id) => ({
    src: photoUrlFor(id),
    description: captionFor(id),
  }));

  return (
    <Box>
      {(value.length > 0 || pendingUploads.length > 0) && (
        <SimpleGrid columns={columns} spacing="12px" marginBottom="12px">
          {value.map((id, index) => (
            <PhotoCard
              key={id}
              photoUrl={photoUrlFor(id)}
              description={captionFor(id)}
              captionMaxLength={captionMaxLength}
              isDisabled={isDisabled}
              isElevated={isElevated}
              isRemoving={removingId === id}
              isRemovalInFlight={removingId != null}
              onDescriptionChange={(description) =>
                setSessionCaptions((current) => ({
                  ...current,
                  [id]: description,
                }))
              }
              // The one write a caption takes: the attachment holds it, so
              // nothing waits for Continue. `onDescriptionSaved` then hands it
              // to the cached draft, which is what this screen re-reads from
              // when it is left and re-entered before the next fetch.
              onDescriptionBlur={() => {
                const description = captionFor(id);

                if (!defersServerWrites) saveDescription(id, description);
                onDescriptionSaved?.(id, description);
              }}
              onRequestRemove={() =>
                defersServerWrites ? dropPhoto(id) : remove(id)
              }
              onOpenViewer={() => setViewerIndex(index)}
            />
          ))}
          {pendingUploads.map((pending) => (
            <PhotoCardSkeleton key={pending.id} isElevated={isElevated} />
          ))}
        </SimpleGrid>
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        accept={allowedExtensions.map((extension) => `.${extension}`).join(",")}
        onChange={(event) => {
          handleFilesSelected(event.target.files);
          event.target.value = "";
        }}
      />
      <Button
        type="button"
        variant="outline"
        colorScheme="seaGreen"
        width="100%"
        height="48px"
        borderRadius="10px"
        leftIcon={<Icon as={Plus} width="14px" height="14px" />}
        isDisabled={isDisabled}
        // Live at the limit too: a disabled button cannot say why it is
        // disabled, and the limit is the one refusal the user can act on
        // (remove a photo, then retry).
        onClick={() =>
          canAddMore ? fileInputRef.current?.click() : notifyLimitReached()
        }
      >
        <FormattedMessage {...messages["taskCreation.photos.addButton"]} />
      </Button>

      <PhotoLightbox
        isOpen={viewerIndex != null}
        index={viewerIndex ?? 0}
        slides={viewerSlides}
        onClose={() => setViewerIndex(null)}
      />
    </Box>
  );
};
